import React, { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Alert, Grid, List, ListItem, ListItemIcon, ListItemText, CircularProgress, Tabs, Tab } from '@mui/material';
import { Business as BusinessIcon, CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import { useSiteStore } from '../../store/site.store';
import { SiteBusinessType } from '../../types/site.types';
import LocalBusinessBadge from '../../components/common/LocalBusinessBadge';
import ActionCard from '../../components/actionQueue/ActionCard';
import { getActions } from '../../services/actionQueueService';

const LocalBusinessPage: React.FC = () => {
  const { selectedSite } = useSiteStore();
  const [activeTab, setActiveTab] = useState(0);

  // Fetch local business actions
  const { data: localActions, isLoading, error } = useQuery({
    queryKey: ['local-business-actions', selectedSite?.organizationId],
    queryFn: () => getActions({ 
      localBusinessOnly: true 
    }, selectedSite?.organizationId),
    enabled: !!selectedSite?.id && selectedSite?.businessType === SiteBusinessType.LOCAL_BUSINESS,
    refetchOnWindowFocus: false
  });

  // Debug logging
  useEffect(() => {
    console.log('LocalBusinessPage - Debug:', {
      siteId: selectedSite?.id,
      organizationId: selectedSite?.organizationId,
      businessType: selectedSite?.businessType,
      isLoading,
      actionsCount: localActions?.length,
      actions: localActions,
      error
    });
  }, [selectedSite, localActions, isLoading, error]);

  if (!selectedSite) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">Please select a site to view local business details</Alert>
      </Box>
    );
  }

  if (selectedSite?.businessType !== SiteBusinessType.LOCAL_BUSINESS) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">This site is not configured as a local business</Alert>
      </Box>
    );
  }

  const focusAreas = [
    {
      title: 'Google Business Profile',
      description: 'Claim and optimize your GBP listing (#1 local ranking factor)',
      details: [
        'Verify your business ownership',
        'Complete all profile information',
        'Add high-quality business photos',
        'Enable messaging and appointment booking',
        'Collect and respond to customer reviews'
      ]
    },
    {
      title: 'LocalBusiness Schema',
      description: 'Add structured data to help search engines understand your business',
      details: [
        'Implement LocalBusiness schema markup',
        'Include business name, address, phone',
        'Add opening hours and service area',
        'Include business contact information',
        'Validate schema with Google\'s testing tool'
      ]
    },
    {
      title: 'NAP Consistency',
      description: 'Ensure Name, Address, Phone are identical across your site and listings',
      details: [
        'Audit your website NAP data',
        'Update all directory listings',
        'Check social media profiles',
        'Monitor third-party business listings',
        'Use citation management tools'
      ]
    },
    {
      title: 'Local Keywords',
      description: 'Track "near me" and city-specific search queries in GSC',
      details: [
        'Monitor location-based search queries',
        'Track "near me" searches',
        'Analyze local competitor keywords',
        'Create location-specific content',
        'Optimize for local search intent'
      ]
    }
  ];

  return (
    <Box p={3}>
      {/* Header */}
      <Typography variant="h4" component="h1" mb={1}>
        Business Optimization
      </Typography>
      <Typography variant="body1" color="text.secondary" mb={4}>
        Your site has been detected as a local business. Follow these optimization areas to improve your local search visibility.
      </Typography>

      {/* Tabs */}
      <Tabs 
        value={activeTab} 
        onChange={(_, newValue) => setActiveTab(newValue)}
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab label="Action Items" />
        <Tab label="Information" />
      </Tabs>

      {/* Tab Content */}
      {activeTab === 0 && (
        <Box>
          {/* Local Business Actions */}
          {isLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading local business actions...</Typography>
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ mb: 4 }}>
              Failed to load local business actions: {error instanceof Error ? error.message : 'Unknown error'}
            </Alert>
          ) : localActions && localActions.length > 0 ? (
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
                🎯 Your Local Business Action Items
              </Typography>
              <Grid container spacing={2}>
                {localActions.map((action) => (
                  <Grid item xs={12} key={action.id}>
                    <ActionCard action={action} />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 4 }}>
              No local business actions found. 
              {selectedSite?.organizationId && ` (Tenant ID: ${selectedSite.organizationId})`}
            </Alert>
          )}
        </Box>
      )}

      {activeTab === 1 && (
        <Box>
          {/* Info Alert */}
          <Alert 
            severity="info" 
            icon={<BusinessIcon />}
            sx={{ 
              mb: 4,
              borderLeft: 4,
              borderColor: 'info.main'
            }}
          >
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Focus on these key areas for local SEO success
            </Typography>
            <Typography variant="body2">
              Local business optimization requires attention to multiple factors. Complete the actions below to improve your local search rankings.
            </Typography>
          </Alert>

          <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mb: 2 }}>
        📚 Local SEO Focus Areas
      </Typography>
      <Grid container spacing={3}>
        {focusAreas.map((area, index) => (
          <Grid item xs={12} md={6} key={index}>
            <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
              <CardContent>
                <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                  <CheckCircleIcon color="success" sx={{ mt: 0.5, flexShrink: 0 }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="h6" fontWeight={600} gutterBottom>
                      {area.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" paragraph>
                      {area.description}
                    </Typography>
                  </Box>
                </Box>

                <Typography variant="subtitle2" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                  Key Actions:
                </Typography>
                <List dense>
                  {area.details.map((detail, detailIndex) => (
                    <ListItem key={detailIndex} sx={{ py: 0.5, px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 24 }}>
                        <CheckCircleIcon sx={{ fontSize: 16, color: 'success.main' }} />
                      </ListItemIcon>
                      <ListItemText 
                        primary={detail}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

          {/* Best Practices */}
          <Card sx={{ mt: 4 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                📋 Local SEO Best Practices
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li><strong>Mobile Optimization:</strong> Most local searches happen on mobile - ensure your site is mobile-friendly</li>
                <li><strong>Page Speed:</strong> Local search results favor fast-loading pages - optimize your performance</li>
                <li><strong>Reviews:</strong> Encourage customers to leave reviews on Google and other platforms</li>
                <li><strong>Local Content:</strong> Create location-specific landing pages and blog content</li>
                <li><strong>Local Backlinks:</strong> Build relationships with local businesses and directories</li>
                <li><strong>Regular Updates:</strong> Keep your business information current across all platforms</li>
              </Box>
            </CardContent>
          </Card>
        </Box>
      )}
    </Box>
  );
};

export default LocalBusinessPage;
